import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Clock, FlaskConical, CalendarCheck, ShoppingCart, Filter, Truck, Fish,
  MapPin, Repeat, Gift, Users, Plus, Minus, ArrowRight, ArrowLeft, Droplet, ShieldCheck,
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";

const WHY = [
  { icon: Clock, title: "Save Time", body: "Skip the trip to your local fish store and have premium aquarium water delivered directly to your door." },
  { icon: FlaskConical, title: "Laboratory Quality", body: "Every batch of water is professionally prepared and tested to provide exceptional consistency for freshwater, saltwater, and reef aquariums." },
  { icon: CalendarCheck, title: "Reliable Weekly Delivery", body: "Choose recurring deliveries or order when needed with scheduled routes covering the entire Phoenix Valley." },
];

const STEPS = [
  { icon: ShoppingCart, title: "Place Your Order", body: "Order online before 5:00 PM the day before your area's scheduled delivery." },
  { icon: Filter, title: "We Prepare Your Water", body: "Your RODI freshwater or premium saltwater is professionally mixed, tested, and prepared for delivery." },
  { icon: Truck, title: "Scheduled Delivery", body: "Your order is delivered directly to your home or business on your area's designated delivery day." },
  { icon: Fish, title: "Enjoy Your Aquarium", body: "Simply perform your water change using professionally prepared water without the hassle of transporting it yourself." },
];

const WATER = [
  { name: "RODI Freshwater", price: "$0.65", unit: "per gallon", body: "Ultra-purified reverse osmosis deionized water ideal for freshwater aquariums, reef top-offs, and custom water preparation." },
  { name: "Instant Ocean Saltwater", price: "$1.45", unit: "per gallon", body: "Professionally mixed to 1.025 specific gravity using Instant Ocean." },
  { name: "Fritz Reef Pro Saltwater", price: "$1.50", unit: "per gallon", body: "Premium reef-quality saltwater mixed to 1.025 specific gravity." },
  { name: "Red Sea Coral Pro", price: "$1.55", unit: "per gallon", body: "Professional reef salt formulated for coral growth and vibrant reef systems." },
  { name: "Catalina Natural Ocean Water", price: "$1.65", unit: "per gallon", body: "Natural ocean saltwater professionally prepared for marine aquariums." },
];

const CONTAINERS = [
  { name: "3 Gallon Container", price: "$13.75" },
  { name: "5 Gallon Container", price: "$14.75" },
  { name: "5 Gallon Jug", price: "$13.95" },
];

const SCHEDULE = [
  { day: "Monday", area: "Area 1", region: "Southeast Valley", color: "#38D6E8", cities: ["Tempe", "Mesa", "Gilbert", "Chandler", "Ahwatukee"] },
  { day: "Tuesday", area: "Area 2", region: "Northeast Valley", color: "#7c9cff", cities: ["Scottsdale", "Paradise Valley", "North Scottsdale", "Cave Creek", "Carefree", "Fountain Hills"] },
  { day: "Wednesday", area: "Area 3", region: "Northwest Valley", color: "#4ade80", cities: ["Glendale", "Peoria", "Surprise", "Sun City", "Arrowhead"] },
  { day: "Thursday", area: "Area 4", region: "Southwest Valley", color: "#fbbf24", cities: ["Downtown Phoenix", "Goodyear", "Buckeye", "Avondale", "Litchfield Park", "West Phoenix"] },
];

const INFO = [
  { title: "Ordering Information", body: "Orders must be placed before 5:00 PM the day before your scheduled delivery. Orders placed after the cutoff automatically move to the following week's route." },
  { title: "Payment", body: "All orders are prepaid by credit card. Drivers do not accept cash or checks." },
  { title: "Minimum Orders", body: "Minimum order value begins at $15 and may vary depending on your delivery area." },
  { title: "Large Deliveries", body: "Orders over 100 gallons require at least five business days' notice and are typically scheduled for Friday or Saturday." },
  { title: "Delivery Policies", body: "Deliveries are generally left at your front door unless alternate instructions are provided. Holiday schedules may shift deliveries by one business day." },
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 44, filter: "blur(6px)" }}
    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Bubbles = () => {
  const bubbles = useMemo(
    () => Array.from({ length: 16 }).map((_, i) => ({
      id: i, left: Math.random() * 100, size: 5 + Math.random() * 20,
      duration: 11 + Math.random() * 12, delay: Math.random() * 10,
    })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {bubbles.map((b) => (
        <motion.span
          key={b.id}
          initial={{ y: "110%", opacity: 0 }}
          animate={{ y: "-15%", opacity: [0, 0.5, 0.5, 0] }}
          transition={{ duration: b.duration, delay: b.delay, repeat: Infinity, ease: "linear" }}
          className="absolute rounded-full border border-[#38D6E8]/30 bg-[#38D6E8]/10 backdrop-blur-[1px]"
          style={{ left: `${b.left}%`, width: b.size, height: b.size }}
        />
      ))}
    </div>
  );
};

export default function WaterDelivery() {
  const navigate = useNavigate();
  const [activeDay, setActiveDay] = useState(null);
  const [openAcc, setOpenAcc] = useState(0);

  const order = () => window.open("https://seatechh2o.com/", "_blank", "noopener,noreferrer");
  const contact = () => navigate("/", { state: { scrollTarget: "bottom" } });

  return (
    <main data-testid="water-delivery-page" className="relative min-h-screen bg-[#071A29] text-[#F7FAFC]">
      {/* Fixed background */}
      <div className="fixed inset-0 z-0">
        <video className="absolute inset-0 h-full w-full object-cover" src="/media/reef-loop.mp4" autoPlay loop muted playsInline data-testid="water-bg-video" />
        <div className="absolute inset-0 bg-[#050d1a]/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#04101c]/86 via-[#0a1626]/72 to-[#04101c]/95" />
      </div>

      {/* Fixed Order Water CTA */}
      <button
        onClick={order}
        data-testid="water-fixed-cta"
        className="fixed bottom-6 left-6 z-[60] inline-flex items-center gap-2 rounded-full bg-[#38D6E8] px-6 py-4 font-body text-xs font-semibold uppercase tracking-[0.16em] text-[#04212a] shadow-[0_10px_40px_-8px_#38D6E8] transition-all duration-300 hover:scale-105"
      >
        <ShoppingCart className="h-4 w-4" /> Order Water
      </button>

      <div className="relative z-10">
        <Navbar />

        {/* HERO */}
        <section className="relative flex min-h-[58vh] items-center overflow-hidden px-6 lg:px-16 pt-52 pb-10">
          <Bubbles />
          <div className="relative mx-auto flex w-full max-w-4xl flex-col items-center text-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#38D6E8]/40 bg-[#38D6E8]/10 px-4 py-2 font-body text-xs font-medium uppercase tracking-[0.28em] text-[#7cebf6]" data-testid="water-kicker">
                RODI Water Delivered to Your Door
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-title text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl" data-testid="water-headline">
                Premium Aquarium Water<br />Delivered to Your Door
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl font-body text-base font-light leading-relaxed text-[#c6d6e4] lg:text-lg">
                Stop hauling heavy water containers from the fish store. Seatech delivers laboratory-quality
                RODI freshwater and premium saltwater directly to your home or business throughout the
                Phoenix Valley.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <button onClick={order} data-testid="water-hero-cta" className="group inline-flex items-center gap-3 rounded-full bg-[#38D6E8] px-8 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#04212a] transition-all duration-300 hover:shadow-[0_0_40px_-8px_#38D6E8]">
                  Order Water
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <button onClick={() => document.getElementById("map")?.scrollIntoView({ behavior: "smooth" })} data-testid="water-hero-secondary" className="inline-flex items-center gap-2 rounded-full border border-[#F7FAFC]/25 px-6 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[#F7FAFC]/90 backdrop-blur-md transition-all duration-300 hover:border-[#F7FAFC]/60">
                  View Delivery Areas
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        {/* WHY CHOOSE */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Why Choose Water Delivery?</h2></Reveal>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {WHY.map((c, i) => (
                <Reveal key={c.title} delay={i * 0.12}>
                  <div className="group h-full rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#38D6E8]/45" data-testid={`water-why-card-${i}`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#38D6E8]/25 bg-[#38D6E8]/10 text-[#7cebf6] transition-all duration-500 group-hover:shadow-[0_0_30px_-6px_#38D6E8]">
                      <c.icon className="h-7 w-7" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-title text-xl font-bold tracking-tight">{c.title}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">{c.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">How It Works</h2></Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.1}>
                  <div className="group relative h-full rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-7 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#38D6E8]/45" data-testid={`water-step-${i}`}>
                    <span className="font-title text-4xl font-bold text-[#38D6E8]/25">0{i + 1}</span>
                    <div className="mt-4 flex h-12 w-12 items-center justify-center rounded-xl border border-[#38D6E8]/25 bg-[#38D6E8]/10 text-[#7cebf6]">
                      <s.icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-5 font-title text-lg font-bold tracking-tight">{s.title}</h3>
                    <p className="mt-2 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">{s.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WATER OPTIONS */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Water Options</h2></Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {WATER.map((w, i) => (
                <Reveal key={w.name} delay={(i % 3) * 0.08}>
                  <div className="group relative h-full overflow-hidden rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#38D6E8]/45 hover:shadow-[0_20px_60px_-30px_#38D6E8]" data-testid={`water-option-${i}`}>
                    <Droplet className="h-8 w-8 text-[#7cebf6]" strokeWidth={1.4} />
                    <h3 className="mt-5 font-title text-lg font-bold tracking-tight">{w.name}</h3>
                    <p className="mt-3 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">{w.body}</p>
                    <div className="mt-6 flex items-baseline gap-2">
                      <span className="font-title text-2xl font-bold text-[#38D6E8]">{w.price}</span>
                      <span className="font-body text-xs font-light uppercase tracking-[0.14em] text-[#c6d6e4]">{w.unit}</span>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WATER CONTAINERS */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Water Containers</h2></Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-3">
              {CONTAINERS.map((c, i) => (
                <Reveal key={c.name} delay={i * 0.1}>
                  <div className="group flex h-full flex-col items-center rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-8 text-center backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#38D6E8]/45" data-testid={`water-container-${i}`}>
                    <h3 className="font-title text-lg font-bold tracking-tight">{c.name}</h3>
                    <span className="mt-2 font-title text-2xl font-bold text-[#38D6E8]">{c.price}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* DELIVERY SCHEDULE + MAP */}
        <section id="map" className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal>
              <span className="font-body text-xs font-medium uppercase tracking-[0.28em] text-[#7cebf6]">Weekly Routes</span>
              <h2 className="mt-3 font-title text-3xl font-bold tracking-tight lg:text-4xl">Delivery Schedule</h2>
            </Reveal>
            <div className="mt-12 grid gap-8 lg:grid-cols-2">
              <div className="grid gap-4">
                {SCHEDULE.map((s) => (
                  <Reveal key={s.day} delay={0.05}>
                    <div
                      onMouseEnter={() => setActiveDay(s.day)}
                      onMouseLeave={() => setActiveDay(null)}
                      data-testid={`water-day-${s.day.toLowerCase()}`}
                      className="cursor-default rounded-2xl border bg-[#0b1626]/50 p-5 backdrop-blur-2xl transition-all duration-300"
                      style={{ borderColor: activeDay === s.day ? s.color : "rgba(247,250,252,0.12)", boxShadow: activeDay === s.day ? `0 0 30px -10px ${s.color}` : "none" }}
                    >
                      <div className="flex items-center gap-3">
                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: s.color, boxShadow: `0 0 12px 2px ${s.color}66` }} />
                        <span className="font-title text-lg font-bold tracking-tight">{s.day}</span>
                        <span className="font-body text-xs font-medium uppercase tracking-[0.16em] text-[#7cebf6]">{s.area} · {s.region}</span>
                      </div>
                      <p className="mt-3 font-body text-sm font-light text-[#c6d6e4]">{s.cities.join(" · ")}</p>
                    </div>
                  </Reveal>
                ))}
              </div>

              <Reveal delay={0.1}>
                <div className="relative overflow-hidden rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-4 backdrop-blur-2xl" data-testid="water-map">
                  <img src="/media/valley-map.gif" alt="Greater Phoenix Valley delivery map" className="w-full rounded-2xl" />
                  <AnimatePresence>
                    {activeDay && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                        className="absolute bottom-6 left-6 rounded-2xl border px-5 py-3 backdrop-blur-md"
                        style={{ borderColor: SCHEDULE.find((s) => s.day === activeDay).color, backgroundColor: "rgba(3,16,29,0.85)" }}
                      >
                        <span className="font-title text-base font-bold" style={{ color: SCHEDULE.find((s) => s.day === activeDay).color }}>
                          {activeDay} — {SCHEDULE.find((s) => s.day === activeDay).region}
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-col items-center gap-4 rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/45 p-8 text-center backdrop-blur-2xl sm:flex-row sm:justify-between sm:text-left" data-testid="water-outside-area">
                <div>
                  <h3 className="font-title text-xl font-bold tracking-tight">Outside our delivery area?</h3>
                  <p className="mt-2 font-body text-sm font-light text-[#c6d6e4]">Contact Seatech Aquariums to learn about expanded delivery options and large-volume deliveries.</p>
                </div>
                <button onClick={contact} data-testid="water-contact-btn" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#38D6E8] px-7 py-3.5 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#04212a] transition-all duration-300 hover:shadow-[0_0_40px_-8px_#38D6E8]">
                  <MapPin className="h-4 w-4" /> Contact Us
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        {/* MEMBERSHIP BENEFITS */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-6xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Membership Benefits</h2></Reveal>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              <Reveal delay={0}>
                <div className="group h-full rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#38D6E8]/45" data-testid="water-member-0">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#38D6E8]/25 bg-[#38D6E8]/10 text-[#7cebf6]"><Repeat className="h-7 w-7" strokeWidth={1.5} /></div>
                  <h3 className="mt-6 font-title text-xl font-bold tracking-tight">Auto Delivery</h3>
                  <p className="mt-3 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">Never worry about running out of aquarium water again. Receive recurring deliveries with discounted pricing and priority scheduling.</p>
                </div>
              </Reveal>
              <Reveal delay={0.12}>
                <div className="group h-full rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#38D6E8]/45" data-testid="water-member-1">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#38D6E8]/25 bg-[#38D6E8]/10 text-[#7cebf6]"><Gift className="h-7 w-7" strokeWidth={1.5} /></div>
                  <h3 className="mt-6 font-title text-xl font-bold tracking-tight">Loyalty Rewards</h3>
                  <p className="mt-3 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">Earn free water after purchasing qualifying gallon amounts.</p>
                  <div className="mt-6">
                    <div className="h-2.5 w-full overflow-hidden rounded-full bg-[#F7FAFC]/10">
                      <motion.div initial={{ width: 0 }} whileInView={{ width: "72%" }} viewport={{ once: true }} transition={{ duration: 1.4, ease: "easeOut" }} className="h-full rounded-full bg-gradient-to-r from-[#38D6E8] to-[#7cebf6]" />
                    </div>
                    <p className="mt-2 font-body text-xs font-light text-[#c6d6e4]">72 / 100 gallons to your next free container</p>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.24}>
                <div className="group h-full rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-8 backdrop-blur-2xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#38D6E8]/45" data-testid="water-member-2">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#38D6E8]/25 bg-[#38D6E8]/10 text-[#7cebf6]"><Users className="h-7 w-7" strokeWidth={1.5} /></div>
                  <h3 className="mt-6 font-title text-xl font-bold tracking-tight">Referral Program</h3>
                  <p className="mt-3 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">Invite friends and family. Both you and your referrals earn rewards toward future water purchases.</p>
                  <div className="mt-6 flex items-center gap-2">
                    {[0, 1, 2].map((n) => (
                      <div key={n} className="flex items-center gap-2">
                        <motion.span initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 + n * 0.15 }} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#38D6E8]/40 bg-[#38D6E8]/10 text-[#7cebf6]"><Users className="h-4 w-4" /></motion.span>
                        {n < 2 && <span className="h-px w-6 bg-[#38D6E8]/40" />}
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* IMPORTANT INFORMATION (accordion) */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto max-w-4xl">
            <Reveal><h2 className="font-title text-3xl font-bold tracking-tight lg:text-4xl">Important Information</h2></Reveal>
            <div className="mt-10 space-y-4">
              {INFO.map((e, i) => {
                const open = openAcc === i;
                return (
                  <Reveal key={e.title} delay={i * 0.06}>
                    <div className="overflow-hidden rounded-2xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 backdrop-blur-2xl" data-testid={`water-info-${i}`}>
                      <button onClick={() => setOpenAcc(open ? -1 : i)} data-testid={`water-info-toggle-${i}`} className="flex w-full items-center justify-between gap-4 px-7 py-6 text-left transition-colors hover:bg-[#38D6E8]/5">
                        <span className="font-title text-lg font-bold tracking-tight">{e.title}</span>
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#38D6E8]/30 bg-[#38D6E8]/10 text-[#7cebf6]">{open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}</span>
                      </button>
                      <AnimatePresence initial={false}>
                        {open && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: "easeInOut" }}>
                            <p className="px-7 pb-6 font-body text-sm font-light leading-relaxed text-[#c6d6e4]">{e.body}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* QUALITY PROMISE */}
        <section className="px-6 lg:px-16 py-24">
          <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <div className="relative overflow-hidden rounded-3xl border border-[#F7FAFC]/12 bg-[#0b1626]/50 p-10 backdrop-blur-2xl">
                <FlaskConical className="h-16 w-16 text-[#7cebf6]" strokeWidth={1.2} />
                <div className="mt-6 space-y-3">
                  {["RODI Purity", "Salinity 1.025", "Alkalinity", "Calcium & Magnesium"].map((label, n) => (
                    <div key={label}>
                      <div className="flex items-center justify-between font-body text-xs font-light text-[#c6d6e4]"><span>{label}</span><span className="text-[#7cebf6]">Optimal</span></div>
                      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-[#F7FAFC]/10">
                        <motion.div initial={{ width: 0 }} whileInView={{ width: `${88 + n * 3}%` }} viewport={{ once: true }} transition={{ duration: 1.2, delay: n * 0.15 }} className="h-full rounded-full bg-gradient-to-r from-[#38D6E8] to-[#7cebf6]" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <span className="font-body text-xs font-medium uppercase tracking-[0.28em] text-[#7cebf6]">Our Quality Promise</span>
              <h2 className="mt-3 font-title text-3xl font-bold tracking-tight lg:text-4xl">Tested Every Day</h2>
              <p className="mt-5 font-body text-sm font-light leading-relaxed text-[#c6d6e4] lg:text-base">
                Every batch of water is professionally prepared and tested before delivery. The same water is
                trusted in Seatech's own aquarium systems, ensuring exceptional quality and consistency for
                your aquatic environment.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#38D6E8]/30 bg-[#38D6E8]/10 px-4 py-2 font-body text-xs font-medium uppercase tracking-[0.16em] text-[#7cebf6]">
                <ShieldCheck className="h-4 w-4" /> Lab-Tested Consistency
              </div>
            </Reveal>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="relative px-6 lg:px-16 py-32">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#020a16]/82 to-[#020a16]/95" />
          <div className="relative mx-auto max-w-3xl text-center">
            <Reveal>
              <h2 className="font-title text-3xl font-bold tracking-tight lg:text-5xl" data-testid="water-final-heading">Exceptional Water. Delivered Effortlessly.</h2>
              <p className="mx-auto mt-6 max-w-2xl font-body text-sm font-light leading-relaxed text-[#c6d6e4] lg:text-base">
                Spend less time hauling water and more time enjoying your aquarium. Whether you maintain a
                freshwater aquarium, saltwater system, or thriving reef, Seatech delivers premium-quality water
                directly to your door with the convenience and reliability you deserve.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <button onClick={order} data-testid="water-final-cta" className="inline-flex items-center gap-3 rounded-full bg-[#38D6E8] px-9 py-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#04212a] transition-all duration-300 hover:shadow-[0_0_44px_-8px_#38D6E8]">Order Water Today</button>
                <button onClick={order} data-testid="water-final-secondary" className="inline-flex items-center gap-2 rounded-full border border-[#F7FAFC]/25 px-8 py-4 font-body text-sm font-light uppercase tracking-[0.16em] text-[#F7FAFC]/90 backdrop-blur-md transition-all duration-300 hover:border-[#F7FAFC]/60">Schedule Auto Delivery</button>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </main>
  );
}
